import type React from "react"
import { useState, useEffect, useRef } from "react"
import styles from "./SearchInput.module.scss"
import { CloseIcon, SearchIcon } from "../../../../assets/icons"

interface SearchInputProps {
  placeholder?: string
  initialValue?: string
  onSearch?: (value: string) => void
  debounceTime?: number
  className?: string
  icon?: React.ReactNode
  autoFocus?: boolean
  // controlled mode
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClear?: () => void
  name?: string
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = "Search",
  initialValue = "",
  onSearch,
  debounceTime = 300,
  className = "",
  icon = <SearchIcon />,
  autoFocus = false,
  value: controlledValue,
  onChange: controlledOnChange,
  onClear: controlledOnClear,
  name,
}) => {
  const isControlled = controlledValue !== undefined
  const [internalValue, setInternalValue] = useState(initialValue)
  const displayValue = isControlled ? controlledValue : internalValue
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (isControlled) {
      controlledOnChange?.(e)
    } else {
      setInternalValue(value)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => onSearch?.(value.trim()), debounceTime)
    }
  }

  const handleClear = () => {
    if (isControlled) {
      controlledOnClear?.()
    } else {
      setInternalValue("")
      onSearch?.("")
    }
    inputRef.current?.focus()
  }

  return (
    <div className={`${styles.searchContainer} ${className}`}>
      <div className={styles.iconContainer}>{icon}</div>
      <input
        ref={inputRef}
        type="text"
        className={styles.searchInput}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleSearchChange}
        name={name}
      />
    </div>
  )
}

export default SearchInput
